import TextUtil from "../../../../../lib/html5-software-library/src/ranj/components/text/TextUtil";
import '../../../../../../src/assets/skins/PreloadAssets'; //Import asset file(s) used by this module.
import FontOffsetParams from "./FontOffsetParams";

export default class FontOffsetCalculator {

    /**
     * Calculate correctional offsets and multipliers for Text objects exported from Adobe Animate.
     * 
     * Make sure fonts are loaded when calling this method. 
     * Call this method before creating skins which contain Text objects. 
     * Typically, call it before showing splash screen.
     * 
     * Required:
     * A movieclip lib.TextPositioningAssets.TextSamplesSkin that contains the following on first and only frame:
     *  - At least 2 textfields with different font sizes per font. More is better.
     *  - First font will be default. Fonts that are not in this movieclip will use the values of the default font. Often, analysing 1 font is enough.
     *  - Best results when using a good spread of font sizes (e.g.: 30, 60, 90, 120). 
     *  - We sample pixels of rendered text. So large font sizes work better because it reduces errors due to anti-aliasing.
     *  - All textfields are aligned with their top-left pixel exactly at 100, 100. More exact results in better fixed positions at runtime.
     *  - All textfields for a font should start with same character. A good practice is to use a capital 'F' or any other character that has a clear top-left point.
     * 
     * @returns object with font names as key and FontPositionFixParams as value
     */
    static calculateFontOffsetParameters() {

        const offsetsPerFont = readFontOffsets();

        //Now we have for each font the offsets by font size
        //Calculate avg offset and multiplier for x and y
        //These can be seen as the y-intersect and slope of a line equation
        const paramsPerFont = {};
        for (const [fontName, params] of Object.entries(offsetsPerFont)) {
            let yOffsetAvg = 0, yMultiplierAvg = 0;
            let xOffsetAvg = 0, xMultiplierAvg = 0;
            let offset, multiplier;
            const n = params.length;
            if (n > 1) {
                params.sort((p1, p2) => p1.fontSize - p2.fontSize);
                //Create all pairs of sizes
                for (let i = 0; i < n - 1; i++) {
                    const { fontSize: size1, dx: dx1, dy: dy1 } = params[i];
                    const { fontSize: size2, dx: dx2, dy: dy2 } = params[i + 1];
                    ({ offset, multiplier } = calcOffsetAndMultiplier(dy1, dy2, size1, size2));
                    yOffsetAvg += offset / (n - 1);
                    yMultiplierAvg += multiplier / (n - 1);
                    ({ offset, multiplier } = calcOffsetAndMultiplier(dx1, dx2, size1, size2));
                    xOffsetAvg += offset / (n - 1);
                    xMultiplierAvg += multiplier / (n - 1);
                }
            }
            paramsPerFont[fontName] = new FontOffsetParams(xOffsetAvg, xMultiplierAvg, yOffsetAvg, yMultiplierAvg);
        }

        return paramsPerFont;        
    }
}

/**
 * Reads offsets of all fonts from an asset exported by Adobe Animate.
 * See requirements in method comment of TextOffsetCalculator.calculateFontParameters
 * @returns 
 */
const readFontOffsets = () => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    const skin = new lib.PreloadAssets.TextSamplesSkin();
    const texts = skin.children.filter(obj => obj.font != null);

    skin.children.forEach(obj => { obj.visible = false; });

    //refX and refY are the coordinate where the Text objects are placed with their top-left-most pixel
    const refX = 100, refY = 100;
    const offsetsPerFont = {};

    texts.forEach(textObj => {
        //Render the current text to the canvas
        textObj.visible = true;
        context.clearRect(0, 0, canvas.width, canvas.height);
        skin.draw(context);
        textObj.visible = false;
        //Find top and left of rendered text
        const w = 150, h = 150;
        const data = context.getImageData(0, 0, w, h).data;
        let minx = canvas.width, miny = canvas.height;
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                const alpha = data[(y * w + x) * 4 + 3];
                if (alpha > 128) {
                    minx = Math.min(x, minx);
                    miny = Math.min(y, miny);
                }
            }
        }
        const fontSize = TextUtil.getFontSize(textObj);
        const fontName = TextUtil.getFontName(textObj);
        const dx = refX - minx, dy = refY - miny;
        if (offsetsPerFont[fontName] == null) {
            offsetsPerFont[fontName] = [];
        }
        offsetsPerFont[fontName].push({ fontSize, dx, dy });
    });
    return offsetsPerFont;
}

const calcOffsetAndMultiplier = (d1, d2, size1, size2) => {
    const multiplier = (d2 - d1) / (size2 - size1);//slope
    const offset = d1 - (multiplier * size1);//y-intercept
    return { offset, multiplier };
}
